import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { JobDTO, JobListItemDTO, JobStatusDTO } from '@/types';

/**
 * True when the job uses the company's default "completed" status and the user lacks
 * jobs.edit.completed (matches job details edit locking).
 */
export function isListItemCompletedLocked(
    job: Pick<JobListItemDTO, 'status' | 'pending_approval'> | Pick<JobDTO, 'status' | 'pending_approval'> | null | undefined,
    jobStatuses: JobStatusDTO[] | undefined,
    canEditCompletedJobs: boolean
): boolean {
    if (canEditCompletedJobs || !job || !jobStatuses?.length) return false;
    
    // Completed jobs that are pending approval can still be edited
    if (job.pending_approval) return false;

    const completedStatus = jobStatuses.find((status: JobStatusDTO) => status.is_default_for_completed_job);
    if (!completedStatus) return false;

    const currentStatusId = typeof job.status === 'string'
        ? findStatusIdByName(jobStatuses, job.status)
        : (job.status as { id?: string } | null | undefined)?.id;

    return currentStatusId === completedStatus.id;
}

function findStatusIdByName(jobStatuses: JobStatusDTO[], statusName: string | undefined): string | undefined {
    if (!statusName) return undefined;
    const target = statusName.trim().toLowerCase();
    return jobStatuses.find((s) => s.name?.trim().toLowerCase() === target)?.id;
}

/**
 * True when the job is in the company's designated "completed" status.
 * Used by the calendar to block drag/resize for everyone (unlike job details, where
 * jobs.edit.completed can allow editing completed jobs).
 */
export function isListItemCompletedForCalendarLock(
    job: Pick<JobListItemDTO, 'status'> | Pick<JobDTO, 'status'> | null | undefined,
    jobStatuses: JobStatusDTO[] | undefined,
): boolean {
    if (!job || !jobStatuses?.length) return false;

    const completedStatus = jobStatuses.find((status: JobStatusDTO) => status.is_default_for_completed_job);
    if (!completedStatus) return false;

    const currentStatusId = typeof job.status === 'string'
        ? findStatusIdByName(jobStatuses, job.status)
        : (job.status as { id?: string } | null | undefined)?.id;

    return currentStatusId === completedStatus.id;
}

/**
 * True when the job is truly completed for settlement purposes: it uses the company's designated
 * completed status AND is not pending approval (mirrors the backend IsJobCompletedAsync).
 */
export function isJobCompleted(
    job: Pick<JobDTO, 'status' | 'pending_approval'> | null | undefined,
    jobStatuses: JobStatusDTO[] | undefined,
): boolean {
    if (!job || job.pending_approval) return false;
    return isListItemCompletedForCalendarLock(job, jobStatuses);
}

export function useIsJobCompleted(job: JobDTO | null | undefined, enabled: boolean = true) {
    const { data: jobStatuses } = useQuery({
        queryKey: ['jobStatuses', job?.company?.id],
        queryFn: () => job?.company?.id ? apiService.getJobStatusesByCompany(job.company.id) : Promise.resolve([]),
        enabled: enabled && !!job?.company?.id,
    });
    return useMemo(() => isJobCompleted(job, jobStatuses), [job, jobStatuses]);
}

export function useCompletedJobEditLock(job: JobDTO | null | undefined, canEditCompletedJobs: boolean, enabled: boolean = true) {
    const { data: jobStatuses } = useQuery({
        queryKey: ['jobStatuses', job?.company?.id],
        queryFn: () => job?.company?.id ? apiService.getJobStatusesByCompany(job.company.id) : Promise.resolve([]),
        enabled: enabled && !!job?.company?.id,
    });

    const isCompletedJobLocked = useMemo(() => {
        return isListItemCompletedLocked(job, jobStatuses, canEditCompletedJobs);
    }, [canEditCompletedJobs, job, jobStatuses]);

    return {
        isCompletedJobLocked,
        jobStatuses,
    };
}